import { isCancelledError, useMutation } from 'react-query'
import { toast } from 'react-toastify'
import { addUserTofund } from 'services/fund'
import posthog from 'posthog-js'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'

/**
 * What to tell the manager about a 400 from POST /poolships/.
 *
 * DRF answers with field errors (`{"member": {"phone_number": [...]}}`,
 * `{"guarantor": [...]}`, `{"can_add_more_than_100_poolships": [...]}`), never
 * a `detail`, so every one of them used to land on «مشکلی پیش آمده!» and the
 * manager had nothing to fix. The field names are also what PostHog gets, so
 * the next report can be read off the dashboard instead of guessed at.
 */
export const describeAddMemberError = (
    data: unknown
): { fields: string[]; message: string } => {
    const fields: string[] = []
    const walk = (value: unknown, path: string) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value as Record<string, unknown>).forEach(
                ([key, inner]) => walk(inner, path ? `${path}.${key}` : key)
            )
        } else if (path) {
            fields.push(path)
        }
    }
    walk(data, '')

    const has = (field: string) =>
        fields.some((f) => f === field || f.startsWith(`${field}.`))
    let message = 'مشکلی پیش آمده! لطفاً دوباره تلاش کنید.'
    if (has('can_add_more_than_100_poolships')) {
        message =
            'این صندوق به سقف ۱۰۰ عضو فعال رسیده است. برای افزودن عضو بیشتر با پشتیبانی تماس بگیرید.'
    } else if (has('member.phone_number')) {
        message =
            'شمارهٔ موبایل پذیرفته نشد. شماره را به شکل ۰۹۱۲۱۲۳۴۵۶۷ وارد کنید.'
    } else if (has('member.first_name') || has('member.last_name')) {
        message = 'نام یا نام خانوادگی پذیرفته نشد. آن را بررسی کنید.'
    } else if (has('guarantor')) {
        message = 'ضامن انتخاب‌شده پذیرفته نشد. ضامن را دوباره انتخاب کنید.'
    } else if (has('recommender')) {
        message = 'معرف انتخاب‌شده پذیرفته نشد. معرف را دوباره انتخاب کنید.'
    } else if (has('number_of_shares')) {
        message = 'تعداد سهم پذیرفته نشد. آن را بررسی کنید.'
    }
    return { fields, message }
}

export default function useSetAddUser() {
    const finishFlow = useFinishFlow(FLOWS.addMember)
    const { mutate, isLoading } = useMutation({
        mutationFn: (payload: IAddNewUserToFundPayload) => {
            return addUserTofund(payload)
        },
        onSuccess: (data, variables) => {
            posthog.capture('member_added', {
                fund_id: variables.moneyPool,
                number_of_shares: variables.number_of_shares,
                has_guarantor: Boolean(variables.guarantor),
                has_recommender: Boolean(variables.recommender),
            })
            toast.success('کاربر با موفقیت اضافه شد')
            // A plain navigate(-1) did nothing when this screen was the first
            // one in the history (a reload, or a mini app opened on it): the
            // member was added, but the manager stayed on a filled-in form and
            // tapped «افزودن عضو» again, which then failed with «هم‌اکنون عضو
            // صندوق است».
            finishFlow(`/fund/${variables.moneyPool}`)
        },
        onError: (error: any, variables) => {
            // A 401 is rejected as a CancelledError on its way to the login
            // page (services/axios); there is nothing to tell the manager here.
            if (isCancelledError(error)) {
                return
            }
            const status = error?.response?.status
            const detail = error?.response?.data?.detail
            const described =
                status === 400 && !detail
                    ? describeAddMemberError(error?.response?.data)
                    : undefined
            posthog.capture('member_add_failed', {
                fund_id: variables?.moneyPool,
                status,
                error_detail: detail,
                error_fields: described?.fields,
                has_guarantor: Boolean(variables?.guarantor),
                has_recommender: Boolean(variables?.recommender),
            })
            // The backend answers 406 with a machine-readable detail when the
            // phone number already has a poolship in this box; without mapping
            // these the user only ever saw the generic toast and kept retrying.
            if (detail === 'Activate existed member.') {
                toast.error(
                    'این شماره قبلاً عضو این صندوق بوده است. برای فعال‌سازی دوباره، از فهرست اعضا روی عضو غیرفعال بزنید و «فعال‌سازی» را انتخاب کنید.',
                    { autoClose: 8000 }
                )
            } else if (detail === 'Poolship already exists.') {
                toast.error('این شماره هم‌اکنون عضو صندوق است.')
            } else if (described) {
                toast.error(described.message, { autoClose: 8000 })
            } else if (!error?.response) {
                // No response at all — the connection dropped. The member may
                // well have been added; say so rather than invite a blind
                // retry that then reports «هم‌اکنون عضو صندوق است».
                toast.error(
                    'پاسخی از سرور نرسید. پیش از تلاش دوباره، فهرست اعضای صندوق را نگاه کنید.',
                    { autoClose: 8000 }
                )
            } else {
                toast.error('مشکلی پیش آمده!')
            }
        },
    })
    return { mutate, isLoading }
}
